import { Api } from '@/Api';
import { useDebug } from '@/composables/useDebug';
import { useVersionsModal } from '@/composables/useVersionsModal';
import { useItemDuplicate } from '@/modules/viewer/composables/useItemDuplicate';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { defineStore } from 'pinia';

const { debug } = useDebug();

export interface ItemVersion {
  id: string;
  links: {
    thumbnailUrl: string;
  };
  lastModifiedAt: string;
  latest: boolean;
}

export interface VersionsState {
  itemVersions: ItemVersion[];
  itemId?: string;
  total?: number;
  loading: boolean;
  pendingActionVersionId: string | null;
  pendingAction: 'duplicate' | 'restore' | null;
}

export const useVersionsStore = defineStore('versionsStore', {
  state: (): VersionsState => ({
    itemVersions: [],
    itemId: undefined,
    total: undefined,
    loading: false,
    pendingActionVersionId: null,
    pendingAction: null,
  }),
  actions: {
    parseList: (data: any) => {
      return (
        data?.map((version: any) => ({
          id: version.id,
          links: version.links,
          lastModifiedAt: version.attributes.lastModifiedAt,
          latest: version.attributes.latest,
        })) || []
      );
    },
    async load(itemId?: string) {
      this.loading = true;
      try {
        if (!itemId) {
          return Promise.reject('No item ID provided');
        }

        this.itemId = itemId;
        const { data, meta } = await Api.item.versions.list(itemId);
        this.$patch({ itemVersions: this.parseList(data?.itemVersions), total: meta?.versionsTotal });
      } catch (e) {
        debug('[VersionsStore] Cannot load versions list');
        debug(e);
      } finally {
        this.loading = false;
      }
    },
    async restore(itemId?: string, versionId?: string, asNew: boolean = false) {
      const { closeVersionsModal } = useVersionsModal();
      const { handleDuplicated } = useItemDuplicate();

      if (!itemId || !versionId) return Promise.reject('No item ID or version ID provided');

      this.pendingActionVersionId = versionId;
      this.pendingAction = asNew ? 'duplicate' : 'restore';

      try {
        const res = await Api.item.versions.restore(itemId, versionId, asNew);
        debug('[VersionsStore] Version restored', res.data);

        if (asNew) {
          handleDuplicated({ storageKey: res.data?.item?.id, redirectUrl: res.data?.item?.links?.viewerUrl });
          // Don't reset pending state — page is navigating away via handleDuplicated
        } else {
          useViewerItemStore().setItem(res.data?.item);
          closeVersionsModal();
          this.pendingActionVersionId = null;
          this.pendingAction = null;
        }
      } catch (e) {
        debug('[VersionsStore] Cannot restore version', e);
        this.pendingActionVersionId = null;
        this.pendingAction = null;
        return false;
      }
    },
  },
  getters: {
    latestVersion: (state: VersionsState) => state.itemVersions.find(version => version.latest),
    restoreList: (state: VersionsState) => {
      return state.itemVersions.filter(version => !version.latest);
    },
    countHiddenVersions: (state: VersionsState) => {
      if (!state.total) {
        return 0;
      }
      return state.total - state.itemVersions.length;
    },
    hasVersionsToRestore: (state: VersionsState) => state.itemVersions.some(version => !version.latest),
    isDuplicatingVersion: (state: VersionsState) => (versionId: string) => state.pendingActionVersionId === versionId && state.pendingAction === 'duplicate',
    isRestoringVersion: (state: VersionsState) => (versionId: string) => state.pendingActionVersionId === versionId && state.pendingAction === 'restore',
    isRestoring: (state: VersionsState) => state.pendingActionVersionId !== null,
  },
});
